import { formatRateChange } from "./formatters";

/**
 * MetricCard's change chip (ANL-E04-S09 AC5, FR-ADM-22, FR-ADM-30). A count changes in relative
 * percent, printed exactly as MetricCard always printed it; a rate changes in points ("+0.4 pt").
 * The arrow follows the direction of the change and the tone follows `goodDirection`, so a rise in
 * a drop-off rate is negative. Colour never carries the meaning alone: the arrow and sign do.
 */

export type GoodDirection = "up" | "down";
export type DeltaTone = "positive" | "negative" | "neutral";

export type MetricDeltaInput = {
  // The server's change: relative percent for a count, points for a rate.
  change: number;
  kind?: "count" | "rate";
  goodDirection?: GoodDirection;
};

const MINUS = "−";

const arrowOf = (change: number): string => (change > 0 ? "↑" : change < 0 ? "↓" : "→");

const toneOf = (change: number, goodDirection: GoodDirection): DeltaTone => {
  if (change === 0) return "neutral";
  return (change > 0) === (goodDirection === "up") ? "positive" : "negative";
};

// The pre-v2 MetricCard text, kept byte for byte so existing importers render unchanged (AC1).
const formatCountDelta = (change: number): string => {
  const sign = change > 0 ? "+" : change < 0 ? MINUS : "";
  return `${sign}${Math.abs(change).toFixed(1)}%`;
};

export const metricDelta = ({ change, kind = "count", goodDirection = "up" }: MetricDeltaInput) => {
  const printed = kind === "rate" ? formatRateChange(change) : formatCountDelta(change);
  return { text: `${arrowOf(change)} ${printed}`, tone: toneOf(change, goodDirection) };
};
